import React from 'react';
import { connect } from 'react-redux';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import { DiApple, DiAndroid } from "react-icons/di";
import PrivacyPolicyModal from "@/components/Modal/PrivacyPolicyModal";
import useHistory from '../../../lib/hooks/useHistory';
import { Version } from '../../composite/Version';
import { Authorized } from '../../composite/Authorized';

interface Props {
    settings: any
}

const Footer: React.FC<Props> = props => {

    const {
        settings
    } = props;

    const history = useHistory();

    const [poweredBySrc, setPoweredBySrc] = React.useState<any>(undefined);

    const [privacyPolicyModalState, setPrivacyPolicyModalState] = React.useState<any>({ open: false, url: undefined });

    React.useEffect(() => {
        if (settings) {
            setPoweredBySrc(settings?.ProductLogoUrl);
        }
    }, [settings])

    // Same pages as the top navbar (My Activity, My Account and My Profiles only when logged in).
    const goTo = (path: string) => () => history.push(path);

    const howItWorksRedirect = () => {
        history.push('/howitworks')
    }

    const twitterSocial = () => {
        window.open(settings.TwitterUrl);
    }

    const facebookSocial = () => {
        window.open(settings.FacebookUrl);
    }

    const instagramSocial = () => {
        window.open(settings.InstagramUrl);
    }

    const linkedInSocial = () => {
        window.open(settings.LinkedInUrl);
    }

    const contactUs = () => {
        window.open(settings.ContactUsUrl);
    }

    const aboutUs = () => {
        window.open(settings.AboutUsUrl);
    }

    const iosApp = () => {
        window.open(settings.IOSAppUrl);
    }

    const androidApp = () => {
        window.open(settings.AndroidAppUrl);
    }

    const ourWebsite = () => {
        window.open(settings.ProductWebsite);
    }

    const handlePrivacyLinkClicked = () => {
        setPrivacyPolicyModalState({ open: true, url: settings?.ClientPrivacyPolicy ? 'ClientPrivacyPolicy' : undefined });
    }

    const handleTermsOfUseClicked = () => {
        setPrivacyPolicyModalState({ open: true, url: 'ClientTermsOfUse', title: 'Terms Of Use' });
    }

    // Light footer by default; a client footer colour override still applies.
    const styles = settings?.Styles ?? {};
    const custom = !!styles.OverrideFooterBackgroundColor;
    const headingColor = styles.OverrideFooterTextColor || (custom ? "#ffffff" : undefined);
    const textColor = styles.OverrideFooterSubTextColor || (custom ? "rgba(255,255,255,0.8)" : undefined);
    const linkStyle = textColor ? { color: textColor } : undefined;

    const FooterLink = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
        <button type="button" className="pb-footer-link" style={linkStyle} onClick={onClick}>{children}</button>
    );

    const Social = ({ url, onClick, label, children }: { url?: string; onClick: () => void; label: string; children: React.ReactNode }) =>
        url ? <button type="button" className="pb-footer-social" aria-label={label} onClick={onClick}>{children}</button> : null;

    const hasServices = settings.AboutUsUrl || settings.ContactUsUrl || settings.AndroidAppUrl || settings.IOSAppUrl;
    const hasSocials = settings.TwitterUrl || settings.FacebookUrl || settings.InstagramUrl || settings.LinkedInUrl;

    return (
        <footer className={"pb-footer" + (custom ? " pb-footer--custom" : "")} style={custom ? { background: styles.OverrideFooterBackgroundColor } : undefined}>
            {privacyPolicyModalState.open && <PrivacyPolicyModal
                onSubmit={() => null}
                isDialogOpened={privacyPolicyModalState.open}
                handleCloseDialog={() => setPrivacyPolicyModalState({ open: false, url: undefined })}
                isViewOnlyMode={true}
                overrideUrl={privacyPolicyModalState.url}
                overrideTitle={privacyPolicyModalState.title}
            />}

            <div className="pb-footer-inner">
                <div className="pb-footer-top">
                    <div className="pb-footer-brand">
                        {settings.VendorLogoUrl && <img src={settings.VendorLogoUrl} alt={settings.VendorName} className="pb-footer-logo" />}
                        <div className="pb-footer-name" style={headingColor ? { color: headingColor } : undefined}>{settings.VendorName}</div>
                        {settings.VendorSlogan && <div className="pb-footer-slogan" style={linkStyle}>{settings.VendorSlogan}</div>}
                    </div>

                    <div className="pb-footer-cols">
                        <div>
                            <div className="pb-footer-heading" style={headingColor ? { color: headingColor } : undefined}>Navigation</div>
                            <FooterLink onClick={goTo('/home')}>Home</FooterLink>
                            <FooterLink onClick={goTo('/auctions')}>{settings.AuctionName}s</FooterLink>
                            <Authorized required={false}>
                                <FooterLink onClick={goTo('/myActivity')}>My Activity</FooterLink>
                                <FooterLink onClick={goTo('/account')}>My Account</FooterLink>
                                <FooterLink onClick={goTo('/biddingProfileList')}>My Profiles</FooterLink>
                            </Authorized>
                            <FooterLink onClick={howItWorksRedirect}>How it Works</FooterLink>
                        </div>

                        <div>
                            <div className="pb-footer-heading" style={headingColor ? { color: headingColor } : undefined}>Support</div>
                            {settings.VendorEmailAddress && (
                                <a className="pb-footer-link" style={linkStyle} href={"mailto:" + settings.VendorEmailAddress}>
                                    <HiOutlineMail /> {settings.VendorEmailAddress}
                                </a>
                            )}
                        </div>

                        {hasServices && (
                            <div>
                                <div className="pb-footer-heading" style={headingColor ? { color: headingColor } : undefined}>Services</div>
                                {settings.AboutUsUrl && <FooterLink onClick={aboutUs}>About us</FooterLink>}
                                {settings.ContactUsUrl && <FooterLink onClick={contactUs}>Contact us</FooterLink>}
                                {(settings.AndroidAppUrl || settings.IOSAppUrl) && (
                                    <div className="pb-footer-apps">
                                        {settings.AndroidAppUrl && <button type="button" className="pb-footer-app" onClick={androidApp}><DiAndroid size={18} /> Android</button>}
                                        {settings.IOSAppUrl && <button type="button" className="pb-footer-app" onClick={iosApp}><DiApple size={18} /> iOS</button>}
                                    </div>
                                )}
                            </div>
                        )}

                        {hasSocials && (
                            <div>
                                <div className="pb-footer-heading" style={headingColor ? { color: headingColor } : undefined}>Connect</div>
                                <div className="pb-footer-note" style={linkStyle}>Stay up to date with our latest updates via our socials!</div>
                                <div className="pb-footer-socials">
                                    <Social url={settings.TwitterUrl} onClick={twitterSocial} label="Twitter"><FaTwitter /></Social>
                                    <Social url={settings.FacebookUrl} onClick={facebookSocial} label="Facebook"><FaFacebook /></Social>
                                    <Social url={settings.InstagramUrl} onClick={instagramSocial} label="Instagram"><FaInstagram /></Social>
                                    <Social url={settings.LinkedInUrl} onClick={linkedInSocial} label="LinkedIn"><FaLinkedinIn /></Social>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="pb-footer-bottom" style={linkStyle}>
                    <div className="pb-footer-powered">
                        Powered by {poweredBySrc && <img src={poweredBySrc} onClick={ourWebsite} alt="PremBid" />}
                    </div>
                    <div className="pb-footer-legal">
                        <FooterLink onClick={handlePrivacyLinkClicked}>Privacy Policy</FooterLink>
                        {settings?.ClientTermsOfUse && <FooterLink onClick={handleTermsOfUseClicked}>Terms Of Use</FooterLink>}
                    </div>
                    <div className="pb-footer-copy">
                        <span>Copyright &copy; R and R Tech (Pty) Ltd - All Rights Reserved</span>
                        <Version />
                    </div>
                </div>
            </div>
        </footer>
    )
}

const mapStateToProps = (state: any) => ({
    settings: state.settings.settings,
})

const mapDispatchToProps = (dispatch: any) => {
    return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
